import type {
  ReportFilters,
  ReportSummary,
  WeeklyRevenuePoint,
  AccountingReport,
  AccountingPaymentLine,
  PaymentMethod,
  AppointmentStatus,
  Payment,
} from '@leoclinic/types';
import { getSupabaseClient } from '../supabase/client';

const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export class ReportsService {
  async getSummary(clinicId: string, filters: ReportFilters): Promise<ReportSummary> {
    const supabase = getSupabaseClient();

    const [{ data: payments }, { data: appointments }] = await Promise.all([
      supabase
        .from('payments')
        .select('amount, method, status')
        .eq('clinic_id', clinicId)
        .eq('status', 'paid')
        .gte('paid_at', filters.from)
        .lte('paid_at', filters.to),
      supabase
        .from('appointments')
        .select('status')
        .eq('clinic_id', clinicId)
        .gte('start_at', filters.from)
        .lte('start_at', filters.to),
    ]);

    const paidPayments = payments ?? [];
    const apts = appointments ?? [];
    const totalRevenue = paidPayments.reduce((s, p) => s + (p.amount ?? 0), 0);
    const completed = apts.filter((a) => a.status === 'completed').length;
    const cancelled = apts.filter((a) => a.status === 'cancelled').length;

    const methodMap = new Map<PaymentMethod, number>();
    for (const p of paidPayments) {
      methodMap.set(p.method, (methodMap.get(p.method) ?? 0) + (p.amount ?? 0));
    }

    const statusMap = new Map<AppointmentStatus, number>();
    for (const a of apts) {
      statusMap.set(a.status, (statusMap.get(a.status) ?? 0) + 1);
    }

    return {
      totalRevenue,
      totalAppointments: apts.length,
      completedAppointments: completed,
      cancelledAppointments: cancelled,
      averageTicket: paidPayments.length ? totalRevenue / paidPayments.length : 0,
      byMethod: [...methodMap.entries()].map(([method, total]) => ({ method, total })),
      byStatus: [...statusMap.entries()].map(([status, count]) => ({ status, count })),
    };
  }

  /** Relatório para contabilidade — exclui pagamentos em dinheiro por defeito */
  async getAccountingReport(clinicId: string, filters: ReportFilters): Promise<AccountingReport> {
    const supabase = getSupabaseClient();

    const { data: payments, error } = await supabase
      .from('payments')
      .select('*')
      .eq('clinic_id', clinicId)
      .eq('status', 'paid')
      .gte('paid_at', filters.from)
      .lte('paid_at', filters.to)
      .order('paid_at', { ascending: true });

    if (error) throw error;

    const all = payments ?? [];
    const cashPayments = all.filter((p) => p.method === 'cash');
    const included = all.filter((p) => p.method !== 'cash');
    const lines = await this.enrichAccountingLines(included);

    const methodMap = new Map<PaymentMethod, { total: number; count: number }>();
    for (const p of included) {
      const current = methodMap.get(p.method) ?? { total: 0, count: 0 };
      methodMap.set(p.method, {
        total: current.total + (p.amount ?? 0),
        count: current.count + 1,
      });
    }

    const totalRevenue = included.reduce((s, p) => s + (p.amount ?? 0), 0);

    return {
      totalRevenue,
      paymentCount: included.length,
      excludedCashTotal: cashPayments.reduce((s, p) => s + (p.amount ?? 0), 0),
      excludedCashCount: cashPayments.length,
      byMethod: [...methodMap.entries()].map(([method, data]) => ({ method, ...data })),
      lines,
    };
  }

  private async enrichAccountingLines(rows: Payment[]): Promise<AccountingPaymentLine[]> {
    if (!rows.length) return [];

    const supabase = getSupabaseClient();
    const clientIds = [...new Set(rows.map((r) => r.client_id))];
    const appointmentIds = [...new Set(rows.map((r) => r.appointment_id).filter(Boolean))] as string[];

    const [{ data: clients }, { data: appointments }] = await Promise.all([
      supabase.from('clients').select('id, full_name').in('id', clientIds),
      appointmentIds.length
        ? supabase.from('appointments').select('id, start_at, treatment_id').in('id', appointmentIds)
        : Promise.resolve({ data: [] }),
    ]);

    const treatmentIds = [...new Set((appointments ?? []).map((a) => a.treatment_id))];
    const { data: treatments } = treatmentIds.length
      ? await supabase.from('treatments').select('id, name').in('id', treatmentIds)
      : { data: [] };

    const clientMap = new Map((clients ?? []).map((c) => [c.id, c.full_name]));
    const aptMap = new Map((appointments ?? []).map((a) => [a.id, a]));
    const treatmentMap = new Map((treatments ?? []).map((t) => [t.id, t.name]));

    return rows.map((row) => {
      const apt = row.appointment_id ? aptMap.get(row.appointment_id) : null;
      const appointmentLabel = apt
        ? `${new Date(apt.start_at).toLocaleDateString('pt-PT')} — ${treatmentMap.get(apt.treatment_id) ?? 'Marcação'}`
        : null;

      return {
        id: row.id,
        paid_at: row.paid_at ?? row.created_at,
        client_name: clientMap.get(row.client_id) ?? '—',
        amount: row.amount ?? 0,
        method: row.method,
        appointment_label: appointmentLabel,
        notes: row.notes,
      };
    });
  }

  async getWeeklyRevenue(clinicId: string): Promise<WeeklyRevenuePoint[]> {
    const supabase = getSupabaseClient();
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 6);
    start.setHours(0, 0, 0, 0);

    const [{ data: payments }, { data: appointments }] = await Promise.all([
      supabase
        .from('payments')
        .select('amount, paid_at')
        .eq('clinic_id', clinicId)
        .eq('status', 'paid')
        .gte('paid_at', start.toISOString()),
      supabase
        .from('appointments')
        .select('start_at')
        .eq('clinic_id', clinicId)
        .gte('start_at', start.toISOString())
        .neq('status', 'cancelled'),
    ]);

    const points: WeeklyRevenuePoint[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayStart.getDate() + 1);

      const revenue = (payments ?? [])
        .filter((p) => {
          const t = new Date(p.paid_at!);
          return t >= dayStart && t < dayEnd;
        })
        .reduce((s, p) => s + (p.amount ?? 0), 0);

      const aptCount = (appointments ?? []).filter((a) => {
        const t = new Date(a.start_at);
        return t >= dayStart && t < dayEnd;
      }).length;

      points.push({
        day: DAY_LABELS[dayStart.getDay()],
        revenue,
        appointments: aptCount,
      });
    }
    return points;
  }
}

export const reportsService = new ReportsService();
